import { HTMLAttributes } from 'react'
import { CoverLetter, Resume, UserDocumentTemplate } from 'packages/types'
import Icon from 'builder/components/Icon'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { formatImageUrl } from 'builder/utils/formatImageUrl'
import { TemplateCategories, TemplateChangeHandler } from 'builder/modules/resumeEditor'
import { useTourConfig } from 'builder/components/DocumentEditor/DocumentTour/hooks/useTourConfig'

import {
  Template,
  TemplatePreview,
  TemplateStatus,
  TemplateName,
  FormatBadges,
  OfflineWrapper,
  PlanBadges,
} from './styles'

type Props = {
  templates: UserDocumentTemplate[]
  selectedTemplates: string[]
  selectedCategory: `${TemplateCategories}`
  updateTemplate: TemplateChangeHandler
  currentDocument: Resume | CoverLetter
} & HTMLAttributes<HTMLDivElement>

export const TemplatesList = (props: Props) => {
  const {
    templates,
    selectedTemplates,
    selectedCategory,
    updateTemplate,
    currentDocument,
    ...rest
  } = props
  const { selectTemplate } = useTourConfig()
  const isOnline = useTypedSelector(state => state.application.isOnline)
  const letterSizeTemplates = ['athens', 'prague', 'shanghai']

  const onUpdateTemplate: TemplateChangeHandler = templateId => {
    updateTemplate(templateId)
    const selectedTemplate = templates.find(t => t.id === templateId)
    selectTemplate(!!selectedTemplate?.supportsCustomColor)
  }

  return (
    <>
      {templates.map((template: UserDocumentTemplate) => {
        if (
          selectedTemplates.length &&
          selectedCategory !== TemplateCategories.all &&
          !selectedTemplates.includes(template.id)
        ) {
          return null
        }

        if (selectedCategory === TemplateCategories.free && template.premium) {
          return null
        }

        return (
          <Template
            key={template.id}
            data-testid={`template-${template.id}`}
            onClick={() => onUpdateTemplate(template.id)}
            {...rest}
          >
            <TemplateName>{template.name}</TemplateName>
            <TemplatePreview
              isActive={currentDocument?.template === template.id}
              image={formatImageUrl(template.thumbnail, { width: 154 })}
              fitStyle={letterSizeTemplates.includes(template.id) ? 'contain' : 'cover'}
            >
              {/* Display supported formats */}
              <FormatBadges badges={template.supportedFormats} />

              {!template.premium && <PlanBadges badges={['Free']} />}

              {isOnline ? (
                <TemplateStatus isActive={currentDocument?.template === template.id}>
                  <Icon.TickLarge />
                </TemplateStatus>
              ) : (
                <OfflineWrapper />
              )}
            </TemplatePreview>
          </Template>
        )
      })}
    </>
  )
}
